import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Building2, 
  UserCheck, 
  Sparkles, 
  ShieldCheck, 
  ArrowRight, 
  ArrowLeft,
  Check
} from 'lucide-react';

const Onboarding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      id: 0,
      title: 'Welcome to',
      highlight: 'Luma Med Global',
      subtitle: 'Medical recruitment by medical professionals',
      description: 'The first AI-powered platform built and operated by medical professionals, connecting exceptional global clinicians with leading healthcare institutions across Saudi Arabia and the Middle East.',
      visual: (
        <div className="relative w-full h-64">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 via-cyan-500/20 to-blue-500/20 rounded-3xl backdrop-blur-sm"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
              <Sparkles className="h-16 w-16 text-white" />
            </div>
          </div>
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="w-24 h-24 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg flex items-center justify-center" style={{ transform: `translateY(${i * 8}px)` }}>
                {i === 0 && <Building2 className="h-10 w-10 text-teal-600" />}
                {i === 1 && <UserCheck className="h-10 w-10 text-cyan-600" />}
                {i === 2 && <ShieldCheck className="h-10 w-10 text-blue-600" />}
                {i === 3 && <Sparkles className="h-10 w-10 text-indigo-600" />}
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 1,
      title: 'For',
      highlight: 'Hospitals & Healthcare Facilities',
      subtitle: 'Hire clinicians safely with medical-led verification',
      description: 'Access pre-verified, medically-screened clinicians. Every candidate undergoes rigorous medical-professional review, AI-powered competency matching, and comprehensive credential verification.',
      features: [
        'Medical-professional credential review',
        'AI-powered candidate matching',
        'Scope of practice verification',
        'Red-flag detection system',
        'Compliance & licensing tracking',
        'Reduced hiring time by 60%'
      ],
      visual: (
        <div className="space-y-4">
          <Card className="p-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Medically Verified</div>
                <div className="text-sm text-gray-600">95% match success rate</div>
              </div>
              <div className="text-3xl font-bold text-teal-600">98</div>
            </div>
          </Card>
          <div className="grid grid-cols-3 gap-3">
            {['ICU', 'Emergency', 'Surgery'].map((dept, i) => (
              <div key={i} className="p-4 bg-white rounded-xl border-2 border-gray-200 text-center hover:border-teal-400 transition-colors cursor-pointer">
                <div className="text-2xl font-bold text-teal-600">{12 + i * 3}</div>
                <div className="text-xs text-gray-600">{dept}</div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: 'For',
      highlight: 'Medical Professionals',
      subtitle: 'Your clear, guided pathway to Saudi Arabia',
      description: 'From document submission to relocation support, we guide you through every step. Our medical-professional team understands your journey because they\'ve been there.',
      features: [
        'AI-powered CV analysis',
        'Video interview with instant feedback',
        'Automated job matching',
        'SCFHS licensing support',
        'Visa & relocation assistance',
        'Transparent, ethical process'
      ],
      visual: (
        <div className="space-y-3">
          {[
            { step: 'Upload Documents', progress: 100, status: 'complete' },
            { step: 'AI Analysis', progress: 100, status: 'complete' },
            { step: 'Medical Review', progress: 75, status: 'active' },
            { step: 'Job Matching', progress: 0, status: 'pending' },
            { step: 'Relocation', progress: 0, status: 'pending' }
          ].map((item, i) => (
            <div key={i} className="p-4 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">{item.step}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  item.status === 'complete' ? 'bg-green-100 text-green-700' :
                  item.status === 'active' ? 'bg-teal-100 text-teal-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {item.status}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    item.status === 'complete' ? 'bg-green-600' :
                    item.status === 'active' ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                  style={{ width: `${item.progress}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 3,
      title: 'Supporting',
      highlight: 'Saudi Vision 2030',
      subtitle: 'Healthcare transformation through excellence',
      description: 'Luma Med Global supports Saudi Arabia\'s Vision 2030 by creating a safe, ethical and medically-verified pathway for global medical professionals to join world-class healthcare institutions.',
      features: [
        'Aligning with Kingdom\'s healthcare goals',
        'World-class talent acquisition',
        'Technology-driven excellence',
        'Regional healthcare leadership',
        'Quality & safety standards',
        'Sustainable healthcare workforce'
      ],
      visual: (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-100 via-cyan-50 to-blue-100 rounded-3xl"></div>
          <div className="relative p-8 text-center">
            <div className="text-7xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent mb-4">
              2030
            </div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Healthcare Excellence</div>
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-2 h-16 bg-gradient-to-t from-teal-600 to-cyan-400 rounded-full" style={{ height: `${40 + i * 8}px` }}></div>
              ))}
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentSlideData = slides[currentSlide];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleGetStarted = (type) => {
    if (type === 'hospital') {
      navigate('/register/employer');
    } else {
      navigate('/register/candidate');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/30 to-cyan-50/30 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left Panel - Content */}
            <div className="p-12 flex flex-col justify-between">
              <div>
                <div className="mb-8">
                  <div className="inline-flex items-center space-x-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Sparkles className="h-4 w-4" />
                    <span>Slide {currentSlide + 1} of {slides.length}</span>
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                    {currentSlideData.title}{' '}
                    <span className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
                      {currentSlideData.highlight}
                    </span>
                  </h1>
                  
                  <p className="text-xl text-gray-600 font-medium mb-6">
                    {currentSlideData.subtitle}
                  </p>
                  
                  <p className="text-gray-700 leading-relaxed mb-8">
                    {currentSlideData.description}
                  </p>
                </div>

                {currentSlideData.features && (
                  <div className="space-y-3">
                    {currentSlideData.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3 text-gray-700">
                        <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-teal-600" />
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="mt-12">
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={handlePrevious}
                    disabled={currentSlide === 0}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Previous
                  </Button>

                  {currentSlide === slides.length - 1 ? (
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => handleGetStarted('hospital')}
                        className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-6"
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        I'm a Hospital
                      </Button>
                      <Button
                        onClick={() => handleGetStarted('professional')}
                        variant="outline"
                        className="border-teal-600 text-teal-600 hover:bg-teal-50 px-6"
                      >
                        <UserCheck className="h-4 w-4 mr-2" />
                        I'm a Medical Professional
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleNext}
                      className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                    >
                      Next
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </Button>
                  )}
                </div>

                {/* Progress Dots */}
                <div className="flex justify-center space-x-2 mt-8">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        currentSlide === index 
                          ? 'w-8 bg-gradient-to-r from-teal-600 to-cyan-600' 
                          : 'w-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right Panel - Visual */}
            <div className="bg-gradient-to-br from-teal-500/5 via-cyan-500/5 to-blue-500/5 p-12 flex items-center justify-center">
              <div className="w-full max-w-md">
                {currentSlideData.visual}
              </div>
            </div>
          </div>
        </Card>

        {/* Skip Button */}
        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
          >
            Skip to Homepage
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;